'use client'

/**
 * ExtraFieldsForm — renders dynamic extra fields from activity.extraFields[].
 * Supports text, textarea, time, number types. Shows bilingual labels.
 *
 * field.label and field.placeholder are { en, es, pt } objects (pre-T1 schema).
 */

import type { ActivityExtraField } from '@/types/availability'
import styles from './ExtraFieldsForm.module.css'

interface ExtraFieldsFormProps {
  fields: ActivityExtraField[]
  values: Record<string, string>
  onChange: (key: string, value: string) => void
  locale: 'en' | 'es' | 'pt'
}

export default function ExtraFieldsForm({ fields, values, onChange, locale }: ExtraFieldsFormProps) {
  if (!fields || fields.length === 0) return null

  return (
    <div className={styles.container}>
      {fields.map((field) => {
        const label = field.label[locale] || field.label.es || field.label.en || ''
        const placeholder = field.placeholder?.[locale] || field.placeholder?.es || ''
        const value = values[field.key] ?? ''
        const inputId = `extra-field-${field.key}`

        return (
          <div key={field.key} className={styles.field}>
            <label htmlFor={inputId} className={styles.label}>
              {label}
              {field.required && <span className={styles.required} aria-label="required"> *</span>}
            </label>

            {field.type === 'textarea' ? (
              <textarea
                id={inputId}
                className={styles.textarea}
                value={value}
                placeholder={placeholder}
                required={field.required}
                onChange={(e) => onChange(field.key, e.target.value)}
                rows={3}
              />
            ) : (
              <input
                id={inputId}
                className={styles.input}
                type={field.type}
                value={value}
                placeholder={placeholder}
                required={field.required}
                onChange={(e) => onChange(field.key, e.target.value)}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
